/**
 * Which leads the app's "Exclude from list" may take, and which the API refuses (PREC-4537).
 *
 * The bulk bar, the row menu, the API and the MCP `exclude_leads` tool must answer the same
 * question: a control the app does not offer must not be reachable by a direct call. This module is
 * that one answer. Its inputs are the two fields every lead view carries — `inclusionStatus` and
 * `assignStatus` — whose values match `InclusionStatus` / `AssignStatus` in the API and the web.
 *
 * The refusal code is the API's error code (`Errors`), so a caller turns a reason into the
 * exception or the user-facing sentence it already has one for.
 */

/** The inclusion buckets "Exclude from list" offers: Found and Not found. */
export const EXCLUDABLE_INCLUSION_STATUSES = {
	ADDED: "ADDED",
	NOT_FOUND: "NOT_FOUND",
} as const;

/** The assignment states "Exclude from list" offers: New and Saved for later. */
export const EXCLUDABLE_ASSIGN_STATUSES = {
	DRAFT: "DRAFT",
	SAVED_FOR_LATER: "SAVED_FOR_LATER",
} as const;

/**
 * What the app's "Exclude from list" sends: the lead moves to Unsure and back to New (DRAFT). The
 * rest of the bulk moves use other targets, so this pair — not the target alone — marks an
 * exclusion request: a move out of the Blacklist to Unsure is an override the API keeps.
 */
export const EXCLUSION_REQUEST_TARGET = {
	inclusionStatus: "UNSURE",
	assignStatus: "DRAFT",
} as const;

/** The fields of a lead the exclusion decision reads. */
export interface LeadExclusionView {
	inclusionStatus: string;
	assignStatus: string;
}

/** The target fields of a bulk status request the exclusion decision reads. */
export interface ExclusionRequestView {
	inclusionStatus?: string | null;
	assignStatus?: string | null;
}

export type LeadExclusionDecision =
	| { allowed: true }
	| { allowed: false; reason: "LEAD_NOT_EXCLUDABLE" };

/**
 * Exclude from list is offered only for a Found or Not found lead in New or Saved for later. A lead
 * in a campaign or otherwise assigned is being worked on (excluding it would also take it off its
 * campaign through the same bulk call), and Excluded/Blacklisted/Unsure leads are already where the
 * action would take them. Null or unknown states are refused, never read as allowed.
 */
export function excludeEligibility(lead: LeadExclusionView): LeadExclusionDecision {
	const inclusionStatus = lead.inclusionStatus as string;
	const assignStatus = lead.assignStatus as string;

	if (
		(inclusionStatus === EXCLUDABLE_INCLUSION_STATUSES.ADDED ||
			inclusionStatus === EXCLUDABLE_INCLUSION_STATUSES.NOT_FOUND) &&
		(assignStatus === EXCLUDABLE_ASSIGN_STATUSES.DRAFT ||
			assignStatus === EXCLUDABLE_ASSIGN_STATUSES.SAVED_FOR_LATER)
	) {
		return { allowed: true };
	}

	return { allowed: false, reason: "LEAD_NOT_EXCLUDABLE" };
}

/** Whether a bulk status request is the "Exclude from list" move, which the API validates. */
export function isExclusionRequest(request: ExclusionRequestView): boolean {
	return (
		request.inclusionStatus === EXCLUSION_REQUEST_TARGET.inclusionStatus &&
		request.assignStatus === EXCLUSION_REQUEST_TARGET.assignStatus
	);
}
